import pricingData from './pricing.json';
import type { PergolaConfig, Anclaje, TipoPergola, Extra } from './types';

/** Una partida del desglose del presupuesto. */
export interface QuoteLine {
  key: string;
  label: string;
  amount: number;
}

/** Resultado completo del presupuesto (estructura lista para serializar a un endpoint). */
export interface QuoteResult {
  currency: string;
  lines: QuoteLine[];
  /** Suma de las partidas antes del precio mínimo. */
  subtotal: number;
  /** true si se aplicó el precio mínimo. */
  minApplied: boolean;
  /** Base imponible (subtotal o precio mínimo). */
  base: number;
  ivaRate: number;
  iva: number;
  /** PVP final con IVA incluido. */
  total: number;
}

interface Pricing {
  currency: string;
  iva: number;
  base_por_m2: number;
  recargos: {
    tipo: Record<string, number>;
    altura: Record<string, number>;
    anclaje: Record<string, number>;
    postes: Record<string, number>;
    color_tier: Record<string, number>;
  };
  extras: Record<string, number>;
  min_precio: number;
}

const PRICING = pricingData as Pricing;

const TIPO_LABEL: Record<TipoPergola, string> = {
  independiente: 'Independiente',
  adosada: 'Adosada a pared',
};
const ANCLAJE_LABEL: Record<Anclaje, string> = {
  hormigon: 'Anclaje a hormigón',
  tarima: 'Anclaje a tarima / terraza',
  cesped: 'Anclaje a césped / terreno',
};
const EXTRA_LABEL: Record<Extra, string> = {
  led: 'Iluminación LED perimetral',
  sensor: 'Sensor de lluvia y viento',
  screens: 'Cortinas / screens laterales',
  motor: 'Motorización de lamas',
};

/**
 * Calcula el presupuesto a partir de la configuración resuelta.
 * Función pura y desacoplada de la UI: depende solo de `pricing.json`,
 * por lo que puede ejecutarse igual en cliente o en un endpoint de servidor.
 */
export function computeQuote(config: PergolaConfig): QuoteResult {
  const lines: QuoteLine[] = [];

  // Base por superficie.
  const areaM2 = (config.widthMm / 1000) * (config.lengthMm / 1000);
  const base = round2(PRICING.base_por_m2 * areaM2);
  lines.push({
    key: 'base',
    label: `Base · ${areaM2.toFixed(2)} m² × ${PRICING.base_por_m2} €/m²`,
    amount: base,
  });

  // Recargo por tipo (adosada descuenta).
  const tipoR = PRICING.recargos.tipo[config.tipo] ?? 0;
  if (tipoR !== 0) lines.push({ key: 'tipo', label: TIPO_LABEL[config.tipo], amount: tipoR });

  // Recargo por altura (según mm; sin recargo en alturas personalizadas no tabuladas).
  const alturaR = PRICING.recargos.altura[String(config.heightMm)] ?? 0;
  if (alturaR !== 0) {
    lines.push({ key: 'altura', label: `Altura ${formatMeters(config.heightMm)} m`, amount: alturaR });
  }

  // Recargo por anclaje.
  const anclajeR = PRICING.recargos.anclaje[config.anclaje] ?? 0;
  if (anclajeR !== 0) lines.push({ key: 'anclaje', label: ANCLAJE_LABEL[config.anclaje], amount: anclajeR });

  // Recargo por postes reforzados.
  const postesKey = config.postes === 6 ? '6-reforzados' : '4-estandar';
  const postesR = PRICING.recargos.postes[postesKey] ?? 0;
  if (postesR !== 0) lines.push({ key: 'postes', label: '6 postes reforzados', amount: postesR });

  // Recargo por color premium.
  const colorR = PRICING.recargos.color_tier[config.colorTier] ?? 0;
  if (colorR !== 0) lines.push({ key: 'color', label: 'Color premium (RAL)', amount: colorR });

  // Extras.
  for (const extra of config.extras) {
    const amount = PRICING.extras[extra] ?? 0;
    if (amount !== 0) lines.push({ key: `extra-${extra}`, label: EXTRA_LABEL[extra] ?? extra, amount });
  }

  const subtotal = round2(lines.reduce((acc, l) => acc + l.amount, 0));
  const minApplied = subtotal < PRICING.min_precio;
  const baseImponible = minApplied ? PRICING.min_precio : subtotal;
  const iva = round2(baseImponible * PRICING.iva);
  const total = round2(baseImponible + iva);

  return {
    currency: PRICING.currency,
    lines,
    subtotal,
    minApplied,
    base: baseImponible,
    ivaRate: PRICING.iva,
    iva,
    total,
  };
}

const EUR_FMT = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
/** Formatea un importe en euros (es-ES). */
export function formatEUR(n: number): string {
  return EUR_FMT.format(n);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function formatMeters(mm: number): string {
  return (mm / 1000).toFixed(1).replace('.', ',');
}
